import {
  FitnessWorkoutFinishError,
  canonicalFitnessWorkoutFinishRequest,
  fitnessWorkoutFinishId,
  hasVerifiedFitnessWorkoutFinishFailedReceipt,
  hasVerifiedFitnessWorkoutFinishSuccess,
  type FitnessWorkoutFinishCommitRequest,
  type FitnessWorkoutFinishFailedReceipt,
  type FitnessWorkoutFinishSuccess,
} from '../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import {
  isGenuineFitnessWorkoutFinishFailedReceipt, isGenuineFitnessWorkoutFinishNotDispatched, isGenuineFitnessWorkoutFinishProof,
} from './fitnessWorkoutFinishGateway';

export type FitnessWorkoutFinishJournalGuard = () => boolean | void;
export interface FitnessWorkoutFinishJournalEntry {
  version: 1;
  key: string;
  ownerId: string;
  workoutId: string;
  request: FitnessWorkoutFinishCommitRequest;
  status: 'pending' | 'succeeded' | 'known_failed';
  result: FitnessWorkoutFinishSuccess | FitnessWorkoutFinishFailedReceipt | null;
  secondaryClaimed: boolean;
  acknowledged: boolean;
}
const DB = 'vyv-fitness-workout-finishes';
const STORE = 'finishes';
// Reads/clones/recovery cannot recreate the authority held by a new claim's actual creator.
const createdEntries = new WeakSet<object>();
// A failed release attempt retains lookup-only recovery; a later context cannot reuse its authority.
const notDispatchedReleaseAttempts = new WeakSet<object>();
const fields = ['version', 'key', 'ownerId', 'workoutId', 'request', 'status', 'result', 'secondaryClaimed', 'acknowledged'] as const;
const keyFor = (owner: string, workout: string) => owner + ':' + workout;
const fail = (code: string): never => { throw new FitnessWorkoutFinishError(code); };
function entryShape(value: unknown): value is FitnessWorkoutFinishJournalEntry {
  return !!value && typeof value === 'object' && !Array.isArray(value) &&
    [Object.prototype, null].includes(Object.getPrototypeOf(value)) && Reflect.ownKeys(value).length === fields.length &&
    fields.every(key => {
      const d = Object.getOwnPropertyDescriptor(value, key);
      return !!d && d.enumerable && 'value' in d;
    });
}
function freeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
}
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort()
    .map(key => [key, stable((value as Record<string, unknown>)[key])]));
  return value;
}
const equal = (a: unknown, b: unknown) => JSON.stringify(stable(a)) === JSON.stringify(stable(b));
function validate(value: unknown, owner: string, workout: string): FitnessWorkoutFinishJournalEntry {
  try {
    if (!entryShape(value)) return fail('finish_storage_unavailable');
    const v = value;
    const request = canonicalFitnessWorkoutFinishRequest(v.request, owner);
    if (v.version !== 1 || v.key !== keyFor(owner, workout) || v.ownerId !== owner || v.workoutId !== workout ||
      request.action !== 'commit' || request.workout_id !== workout || !equal(v.request, request) ||
      !['pending', 'succeeded', 'known_failed'].includes(v.status) ||
      typeof v.secondaryClaimed !== 'boolean' || typeof v.acknowledged !== 'boolean' ||
      (v.status !== 'succeeded' && (v.secondaryClaimed || v.acknowledged))) return fail('finish_storage_unavailable');
    if (v.status === 'pending' ? v.result !== null : v.status === 'succeeded'
      ? !hasVerifiedFitnessWorkoutFinishSuccess(request, v.result, owner)
      : !hasVerifiedFitnessWorkoutFinishFailedReceipt(request, v.result, owner)) return fail('finish_storage_unavailable');
    // A durable result is retained as data only. Storage cannot recreate gateway authority.
    return freeze(JSON.parse(JSON.stringify({ ...v, request })) as FitnessWorkoutFinishJournalEntry);
  } catch { return fail('finish_storage_unavailable'); }
}
function original(entry: FitnessWorkoutFinishJournalEntry) {
  if (!entryShape(entry)) return fail('finish_storage_unavailable');
  const owner = fitnessWorkoutFinishId(entry.ownerId);
  const workout = fitnessWorkoutFinishId(entry.workoutId);
  return validate(entry, owner, workout);
}
function matches(a: FitnessWorkoutFinishJournalEntry, b: FitnessWorkoutFinishJournalEntry) {
  return a.key === b.key && a.ownerId === b.ownerId && a.workoutId === b.workoutId && equal(a.request, b.request);
}

/** Dedicated transactional journal. Unknown outcomes remain claimed; recovery never dispatches a commit. */
export function createFitnessWorkoutFinishJournal(factory?: IDBFactory) {
  async function transaction<T>(
    mode: IDBTransactionMode,
    guard: FitnessWorkoutFinishJournalGuard,
    work: (store: IDBObjectStore, finish: (value: T) => void, abort: (error: unknown) => void, check: () => void) => void,
  ): Promise<T> {
    if (typeof guard !== 'function') return fail('invalid_session');
    const assert = () => { if (guard() === false) fail('invalid_session'); };
    assert();
    // Resolve the production factory when an operation begins, including after route remount.
    const capturedFactory = factory ?? globalThis.indexedDB;
    if (!capturedFactory) return fail('finish_storage_unavailable');
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false;
      const request = capturedFactory.open(DB, 1);
      const rejectOpen = (error: unknown) => { if (!settled) { settled = true; reject(error); } };
      request.onupgradeneeded = () => {
        try {
          assert();
          request.result.createObjectStore(STORE, { keyPath: 'key' });
        } catch (error) { request.transaction?.abort(); rejectOpen(error); }
      };
      request.onsuccess = () => {
        if (settled) { request.result.close(); return; }
        try { assert(); settled = true; resolve(request.result); }
        catch (error) { request.result.close(); rejectOpen(error); }
      };
      request.onerror = request.onblocked = () => rejectOpen(new FitnessWorkoutFinishError('finish_storage_unavailable'));
    });
    try { assert(); } catch (error) { db.close(); throw error; }
    return new Promise<T>((resolve, reject) => {
      let tx: IDBTransaction;
      try { tx = db.transaction(STORE, mode); } catch { db.close(); reject(new FitnessWorkoutFinishError('finish_storage_unavailable')); return; }
      let result: T;
      let finished = false;
      let reason: unknown;
      const abort = (error: unknown) => {
        reason = error;
        try { tx.abort(); } catch { db.close(); reject(reason); }
      };
      const check = () => { try { assert(); } catch (error) { abort(error); throw error; } };
      tx.oncomplete = () => {
        db.close();
        try { assert(); if (!finished) fail('finish_storage_unavailable'); resolve(result); }
        catch (error) { reject(error); }
      };
      tx.onabort = tx.onerror = () => { db.close(); reject(reason ?? new FitnessWorkoutFinishError('finish_storage_unavailable')); };
      try {
        check();
        work(tx.objectStore(STORE), value => { check(); result = value; finished = true; }, abort, check);
      } catch (error) { abort(error); }
    });
  }
  function guarded(callback: () => void, abort: (error: unknown) => void) {
    return () => { try { callback(); } catch (error) { abort(error); } };
  }
  async function read(ownerId: string, workoutId: string, guard: FitnessWorkoutFinishJournalGuard) {
    const owner = fitnessWorkoutFinishId(ownerId), workout = fitnessWorkoutFinishId(workoutId);
    return transaction<FitnessWorkoutFinishJournalEntry | null>('readonly', guard, (store, finish, abort, check) => {
      const request = store.get(keyFor(owner, workout));
      request.onsuccess = guarded(() => { check(); finish(request.result === undefined ? null : validate(request.result, owner, workout)); }, abort);
    });
  }
  async function claim(
    ownerId: string,
    input: FitnessWorkoutFinishCommitRequest,
    guard: FitnessWorkoutFinishJournalGuard,
    failedReceipt?: FitnessWorkoutFinishFailedReceipt,
  ) {
    const owner = fitnessWorkoutFinishId(ownerId);
    const request = canonicalFitnessWorkoutFinishRequest(input, owner);
    if (request.action !== 'commit') return fail('review_required');
    const target = request.workout_id;
    const proposed = validate({ version: 1, key: keyFor(owner, target), ownerId: owner, workoutId: target,
      request, status: 'pending', result: null, secondaryClaimed: false, acknowledged: false }, owner, target);
    // Capture actual proof identity, rather than trusting a caller's later-mutated reference.
    const retryProof = failedReceipt;
    const claimed = await transaction<{ entry: FitnessWorkoutFinishJournalEntry; created: boolean }>('readwrite', guard, (store, finish, abort, check) => {
      const request = store.get(proposed.key);
      request.onsuccess = guarded(() => {
        check();
        if (request.result !== undefined) {
          const existing = validate(request.result, owner, target);
          const freshKnownFailure = existing.status === 'known_failed' && retryProof !== undefined &&
            isGenuineFitnessWorkoutFinishFailedReceipt(retryProof) &&
            hasVerifiedFitnessWorkoutFinishFailedReceipt(existing.request, retryProof, owner) &&
            existing.result?.receipt_id === retryProof.receipt_id &&
            proposed.request.operation_id !== existing.request.operation_id;
          if (!freshKnownFailure) { finish({ entry: existing, created: false }); return; }
          check(); store.put(proposed);
        } else { check(); store.add(proposed); }
        const verified = store.get(proposed.key);
        verified.onsuccess = guarded(() => {
          check();
          const saved = validate(verified.result, owner, target);
          if (!matches(saved, proposed) || saved.status !== 'pending') return fail('finish_storage_unavailable');
          finish({ entry: saved, created: true });
        }, abort);
      }, abort);
    });
    if (claimed.created) createdEntries.add(claimed.entry);
    return claimed;
  }
  async function settle(
    input: FitnessWorkoutFinishJournalEntry,
    proof: FitnessWorkoutFinishSuccess | FitnessWorkoutFinishFailedReceipt,
    guard: FitnessWorkoutFinishJournalGuard,
  ) {
    const entry = original(input);
    const success = isGenuineFitnessWorkoutFinishProof(proof) && hasVerifiedFitnessWorkoutFinishSuccess(entry.request, proof, entry.ownerId);
    const failure = isGenuineFitnessWorkoutFinishFailedReceipt(proof) && hasVerifiedFitnessWorkoutFinishFailedReceipt(entry.request, proof, entry.ownerId);
    if (!success && !failure) return fail('outcome_unknown');
    return transaction<FitnessWorkoutFinishJournalEntry>('readwrite', guard, (store, finish, abort, check) => {
      const request = store.get(entry.key);
      request.onsuccess = guarded(() => {
        check();
        const existing = validate(request.result, entry.ownerId, entry.workoutId);
        if (!matches(existing, entry)) return fail('finish_pending_action');
        const status = success ? 'succeeded' : 'known_failed';
        if (existing.status !== 'pending') {
          if (existing.status !== status || existing.result?.receipt_id !== proof.receipt_id) return fail('finish_pending_action');
          finish(existing); return;
        }
        const settled = validate({ ...existing, status, result: proof }, entry.ownerId, entry.workoutId);
        check(); store.put(settled);
        const verified = store.get(entry.key);
        verified.onsuccess = guarded(() => {
          check(); const saved = validate(verified.result, entry.ownerId, entry.workoutId);
          if (!equal(saved, settled)) return fail('finish_storage_unavailable');
          finish(saved);
        }, abort);
      }, abort);
    });
  }
  async function claimSecondary(
    input: FitnessWorkoutFinishJournalEntry,
    proof: FitnessWorkoutFinishSuccess,
    guard: FitnessWorkoutFinishJournalGuard,
  ) {
    const entry = original(input);
    if (!isGenuineFitnessWorkoutFinishProof(proof) || !hasVerifiedFitnessWorkoutFinishSuccess(entry.request, proof, entry.ownerId)) return fail('outcome_unknown');
    return transaction<{ entry: FitnessWorkoutFinishJournalEntry; claimed: boolean }>('readwrite', guard, (store, finish, abort, check) => {
      const request = store.get(entry.key);
      request.onsuccess = guarded(() => {
        check(); const existing = validate(request.result, entry.ownerId, entry.workoutId);
        if (!matches(existing, entry) || existing.status !== 'succeeded' || existing.result?.receipt_id !== proof.receipt_id) return fail('finish_pending_action');
        if (existing.secondaryClaimed || existing.acknowledged) { finish({ entry: existing, claimed: false }); return; }
        const claimed = validate({ ...existing, secondaryClaimed: true }, entry.ownerId, entry.workoutId);
        check(); store.put(claimed);
        const verified = store.get(entry.key);
        verified.onsuccess = guarded(() => {
          check(); const saved = validate(verified.result, entry.ownerId, entry.workoutId);
          if (!equal(saved, claimed)) return fail('finish_storage_unavailable');
          finish({ entry: saved, claimed: true });
        }, abort);
      }, abort);
    });
  }
  async function acknowledge(input: FitnessWorkoutFinishJournalEntry, guard: FitnessWorkoutFinishJournalGuard) {
    const entry = original(input);
    return transaction<{ entry: FitnessWorkoutFinishJournalEntry; acknowledged: boolean }>('readwrite', guard, (store, finish, abort, check) => {
      const request = store.get(entry.key);
      request.onsuccess = guarded(() => {
        check(); const existing = validate(request.result, entry.ownerId, entry.workoutId);
        if (!matches(existing, entry) || existing.status !== 'succeeded') return fail('finish_pending_action');
        if (existing.acknowledged) { finish({ entry: existing, acknowledged: false }); return; }
        const acknowledged = validate({ ...existing, acknowledged: true }, entry.ownerId, entry.workoutId);
        check(); store.put(acknowledged);
        const verified = store.get(entry.key);
        verified.onsuccess = guarded(() => {
          check(); const saved = validate(verified.result, entry.ownerId, entry.workoutId);
          if (!equal(saved, acknowledged)) return fail('finish_storage_unavailable');
          finish({ entry: saved, acknowledged: true });
        }, abort);
      }, abort);
    });
  }
  async function releaseNotDispatched(
    input: FitnessWorkoutFinishJournalEntry,
    error: unknown,
    freshGuard: FitnessWorkoutFinishJournalGuard,
  ): Promise<{ released: boolean }> {
    if (!input || typeof input !== 'object' || !createdEntries.has(input)) return fail('outcome_unknown');
    const entry = original(input);
    if (entry.status !== 'pending' || !isGenuineFitnessWorkoutFinishNotDispatched(error, entry.request, entry.ownerId)) {
      return fail('outcome_unknown');
    }
    const authority = error as object;
    if (notDispatchedReleaseAttempts.has(authority)) return fail('outcome_unknown');
    notDispatchedReleaseAttempts.add(authority);
    const guard = () => {
      if (!createdEntries.has(input) || !isGenuineFitnessWorkoutFinishNotDispatched(error, entry.request, entry.ownerId)) return fail('outcome_unknown');
      return freshGuard();
    };
    return transaction<{ released: boolean }>('readwrite', guard, (store, finish, abort, check) => {
      const request = store.get(entry.key);
      request.onsuccess = guarded(() => {
        check();
        if (request.result === undefined) { finish({ released: false }); return; }
        const existing = validate(request.result, entry.ownerId, entry.workoutId);
        if (!matches(existing, entry) || existing.status !== 'pending') return fail('finish_pending_action');
        check(); store.delete(entry.key);
        const verified = store.get(entry.key);
        verified.onsuccess = guarded(() => {
          check();
          if (verified.result !== undefined) return fail('finish_storage_unavailable');
          finish({ released: true });
        }, abort);
      }, abort);
    });
  }
  return { read, claim, settle, claimSecondary, acknowledge, releaseNotDispatched };
}
